import { useContext, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { LayoutGrid, Users } from 'lucide-react'
import { isPlatformUser, AuthContext } from '@retink/web-auth'
import { api, signOut } from '../lib/api'
import { type NavItem } from '@retink/web-ui'
import { ConsoleShell } from './ConsoleShell'

// The tenant admin's home surface — a tenant admin governs their OWN tenant from the
// console (locked 3-role model), same shell as the super/org views but scoped to one
// tenant and with NO Platform strip. Platform / org admins who drill in also land
// here (they outrank tenant scope); ConsoleShell gates the bronze strip on
// isPlatformUser, so a native tenant admin never sees the god-mode cues.
export function TenantAdminShell() {
  const { user } = useContext(AuthContext)
  const [tenantName, setTenantName] = useState<string | null>(null)

  const isPlatform = isPlatformUser(user)
  // Same predicate the server's requireTenantAdmin enforces — UX gate only.
  const canAdmin = isPlatform || user?.orgRole === 'admin' || user?.tenantRole === 'admin'

  useEffect(() => {
    api.get<{ name: string }>('/api/tenant-admin/tenant')
      .then((t) => setTenantName(t.name))
      .catch(() => setTenantName(null))
  }, [])

  // Rank alone isn't enough — this surface scopes everything off the token's
  // tenant claim. Staff arrive with one only via switch-tenant (Enter Workspace);
  // a bare staff session (e.g. a stale /tenant-home bookmark) bounces home
  // instead of rendering a tenant-less dead end.
  if (!canAdmin || !user?.tenantId) return <Navigate to="/" replace />

  const nav: NavItem[] = [
    { to: '/tenant-home/overview', icon: LayoutGrid, label: 'Overview' },
    { to: '/tenant-home/team', icon: Users, label: 'Team' },
  ]

  return (
    <ConsoleShell
      nav={nav}
      onSignOut={signOut}
      navGroup={{ label: 'Tenant', entity: tenantName ?? undefined }}
      // A native tenant admin gets an honest label; platform staff keep their
      // platformRole label (falls through to ConsoleShell's default).
      roleLabel={!isPlatform && user?.orgRole !== 'admin' ? 'tenant admin' : undefined}
      accountPath="/tenant-home/account"
      outletContext={{ tenantName }}
    />
  )
}
